/**
 * FERP Portal - Component Map (DRR-045)
 *
 * Maps component string names (from SubgraphMetadata.routes[].component)
 * to actual React components.
 *
 * FERP Principles:
 * - Data-driven: Route definitions come from backend
 * - Component registry: Frontend maps string names to components
 *
 * Naming Convention:
 * - Component names match SubgraphMetadata.routes[].component
 * - Subgraph IDs match SubgraphMetadata.subgraph_id (e.g., 'accounting')
 *
 * NOTE: This is necessary because React can't dynamically import
 * components by string name at runtime. When adding new subgraphs,
 * register their page components here.
 */

import React from 'react';

// Core pages (DRR-072: Backend returns ferp-core routes in allRoutes)
import { DashboardPage } from '@/pages/DashboardPage';
import { MyInboxPage } from '@/pages/inbox';
import { TaskListPage, TaskListPage as CoreTaskListPage, TaskTemplatesPage } from '@/pages/tasks';
import { MyTrainingPage, StaffTrainingPage, TrainingComplianceDashboard } from '@/pages/training';
import { PolicyListPage as CorePolicyListPage } from '@/pages/policies';
import { MetricListPage as CoreMetricListPage } from '@/pages/metrics';
import { QueueManager as CoreQueueManager } from '@/components/admin';
import { SubgraphConstellation, PrimitiveGrid as CorePrimitiveConstellationPage } from '@/components/architect';
import { MasterWorkflowPage, WorkflowListPage, WorkflowRegistryPage, LlmWorkflowEditorPage, WorkflowDetailPage, WorkflowStudioPage } from '@/pages/workflows';
import { LlmFormSchemaEditorPage, FormGalleryPage } from '@/pages/forms';
import { UnifiedPrimitivePage } from '@/pages/primitives';
import { ComponentExplorerPage } from '@/pages/explorer';
import { ErrorMonitorPage } from '@/pages/ErrorMonitorPage';

// Core primitive list pages (referenced by Rust seed data component names)
import { StateListPage } from '@/pages/states';
import { EventListPage } from '@/pages/events';
import { SagaListPage } from '@/pages/sagas';
import { DAGNodeListPage } from '@/pages/dagnodes';
import { AlertListPage } from '@/pages/alerts';
import { ActorListPage } from '@/pages/actors';
import { RoleListPage } from '@/pages/roles';
import { ComplianceListPage } from '@/pages/compliance';
import { RiskListPage } from '@/pages/risks';
import { EvidenceListPage } from '@/pages/evidence';
import { GanttPage } from '@/pages/gantt';
import { CalendarPage } from '@/pages/calendar/CalendarPage';
import { WorkflowOrchestrationPage } from '@/pages/workflow-orchestration';
import { WorkflowStateView } from '@/pages/workflow-state';
import { DataSourceBrowserPage } from '@/pages/data-sources';
import { ConstellationPage } from '@/pages/constellation';
import { JourneyPage } from '@/pages/journey';
import { FormSchemaAdminPage, SourceMapListPage, ProductCatalogPage, PatientSafetyDashboard, WebsiteSettingsPage } from '@/pages/admin';
import SubgraphEntityManagerPage from '@/pages/admin/SubgraphEntityManagerPage';
import { ScopeHierarchyPage } from '@/pages/scope/ScopeHierarchyPage';
import { MyAccessPage } from '@/pages/scope/MyAccessPage';
import WorkflowObserverPage from '@/pages/architect/WorkflowObserverPage';
import { DevDashboardPage } from '@/pages/dev/DevDashboardPage';
import { TenantManagementPage as TenantOnboardingPageComponent } from '@/pages/tenant-management';
import { ExchangePage } from '@/pages/exchange';
import { LedgerDashboardPage, LedgerFamilyPage, LedgerAuditPage } from '@/pages/ledger';
import { RegistryExplorerPage } from '@/pages/architect/RegistryExplorerPage';
import { ActorManagementPage } from '@/pages/actors';
import { ReferenceLibraryPage } from '@/pages/reference';

// Subgraph pages — generic data-driven pages handle ALL subgraphs/entity types
import {
  PayrollPage,
  SubgraphBrowserPage,
  SubgraphEntityFormPage,
} from '@/pages/subgraphs';

// Workforce subgraph pages (@ferp/workforce)
import {
  WorkforceDashboardPage,
  EmployeeListPage as WfEmployeeListPage,
  EmployeeCreatePage as WfEmployeeCreatePage,
  EmployeeDetailPage as WfEmployeeDetailPage,
  EmployeeEditPage as WfEmployeeEditPage,
  PayrollListPage as WfPayrollListPage,
  PayrollRunPage as WfPayrollRunPage,
  PayrollRunDetailPage as WfPayrollRunDetailPage,
  PayStubListPage as WfPayStubListPage,
  PayStubDetailPage as WfPayStubDetailPage,
  AmendmentListPage as WfAmendmentListPage,
  PayPeriodListPage as WfPayPeriodListPage,
  TimeAttendancePage as WfTimeAttendancePage,
  GpsPunchPage as WfGpsPunchPage,
  WhosInPage as WfWhosInPage,
  ExceptionsPage as WfExceptionsPage,
  TimesheetVerificationPage as WfTimesheetVerificationPage,
  SchedulePage as WfSchedulePage,
  ScheduleCalendarPage as WfScheduleCalendarPage,
  ScheduleTemplatesPage as WfScheduleTemplatesPage,
  ScheduleTemplatePage as WfScheduleTemplatePage,
  LeaveBalancePage as WfLeaveBalancePage,
  RequestListPage as WfRequestListPage,
  RequestCreatePage as WfRequestCreatePage,
  PolicyListPage as WfPolicyListPage,
  PolicyDetailPage as WfPolicyDetailPage,
  AccrualPolicyPage as WfAccrualPolicyPage,
  CompanyPage as WfCompanyPage,
  CompanySettingsPage as WfCompanySettingsPage,
  BranchListPage as WfBranchListPage,
  DepartmentListPage as WfDepartmentListPage,
  QualificationListPage as WfQualificationListPage,
  KpiListPage as WfKpiListPage,
  ReviewListPage as WfReviewListPage,
  ReviewDetailPage as WfReviewDetailPage,
  ReportsPage as WfReportsPage,
  ReportListPage as WfReportListPage,
  ReportResultPage as WfReportResultPage,
  DocumentListPage as WfDocumentListPage,
  DocumentDetailPage as WfDocumentDetailPage,
  EmployeeDocumentsPage as WfEmployeeDocumentsPage,
  GeoFenceListPage as WfGeoFenceListPage,
  GeoFenceDetailPage as WfGeoFenceDetailPage,
  GeoFenceViolationsPage as WfGeoFenceViolationsPage,
  JobListPage as WfJobListPage,
  JobDetailPage as WfJobDetailPage,
  JobCostReportPage as WfJobCostReportPage,
  InvoiceListPage as WfInvoiceListPage,
  InvoiceDetailPage as WfInvoiceDetailPage,
  ExpenseListPage as WfExpenseListPage,
  ExpenseReportPage as WfExpenseReportPage,
  JobPostingListPage as WfJobPostingListPage,
  ApplicantDetailPage as WfApplicantDetailPage,
  InterviewSchedulePage as WfInterviewSchedulePage,
  OnboardingListPage as WfOnboardingListPage,
  OnboardingDetailPage as WfOnboardingDetailPage,
  SmartOnboardingPage as WfSmartOnboardingPage,
  TaxFilingCenterPage as WfTaxFilingCenterPage,
  AuditLogPage as WfAuditLogPage,
  MyAccountPage as WfMyAccountPage,
  ImportExportPage as WfImportExportPage,
} from '@ferp/workforce';

// Intake Studio (DRR-137: Multi-modal ingestion hub; renamed from Document Studio DRR-072)
import { DocumentStudioPage, IntakeStudioPage } from '@/pages/document-studio';

// Food Service Subgraph (DRR-280: Ghost Kitchen Operations)
import {
  StationView,
  ExpoQueueView,
  KitchenManagerDashboard,
  DriverDeliveryView,
} from '@/pages/food-service';

// CFSS Subgraph (DRR-279: Community First Services and Supports)
import {
  CfssDashboardPage,
  CfssTimesheetApprovalPage,
  CfssSupervisionPage,
  CfssPayrollPage,
  CfssWorkerManagementPage,
  CfssServiceAuthPage,
  CfssTrainingPage,
  CfssDocumentsPage,
  CfssIncidentPage,
  CfssCommunicationLogPage,
} from '@/pages/cfss';

// PDF Studio (DRR-079: Full PDF Suite entry point)
import { PdfStudioPage } from '@/pages/pdf-studio';

// Core pages available as existing implementations
import { MyDocumentsPage } from '@/pages/documents';
import { RoleAccessComparisonPage } from '@/pages/admin';
import { ApprovalListPage } from '@/pages/approvals';

// Compliance pages (DRR-058, DRR-090, DRR-112)
import { RegulatoryRulesPage, RuleSetViewerPage, RuleSetAdminPage, StandardsPackPage } from '@/pages/compliance';

// DRR-207: CONFIGURE section pages (Data vs Configuration separation)
import { FormSchemaOverrideListPage, FormSchemaOverrideDetailPage } from '@/pages/form-schema-overrides';
import { RegulatoryGovernancePage } from '@/pages/admin/RegulatoryGovernancePage';
import { RegulatoryOverviewPage } from '@/pages/configure/RegulatoryOverviewPage';

// Witness queue (DRR-063 Tier 4 dual verification)
import { WitnessQueuePage } from '@/pages/admin';

// Offline chain sync dashboard (DRR-113)
import { SyncDashboardPage } from '@/pages/sync';

// Beacon / elopement alert dashboard (DRR-100/108) — overlaps with existing AlertListPage import above
import { BeaconDashboardPage } from '@/pages/alerts';

// Developer / architect tools
import { ConfigEdgeBrowserPage, RegistryExplorerPage as DevRegistryExplorerPage } from '@/pages/dev';

// Template version history (DRR-089 versioning)
import { TemplateVersionPage } from '@/pages/templates';

// Notification page
import { NotificationListPage } from '@/pages/notifications';

// Report page (core)
import { ReportListPage as CoreReportListPage } from '@/pages/reports';

// Team Management (DRR-061)
import { TeamManagementPage } from '@/pages/team';

// Placeholder component for routes not yet implemented
const PlaceholderPage: React.FC<{ componentName: string }> = ({ componentName }) => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h2>Component: {componentName}</h2>
    <p>This page is defined in SubgraphMetadata but not yet implemented.</p>
  </div>
);

/**
 * Map of component names to React components.
 *
 * Component names match those stored in SubgraphMetadata.routes[].component
 */
export const COMPONENT_MAP: Record<string, React.ComponentType<unknown>> = {
  // Core pages (ferp-core subgraph — backend returns these in allRoutes)
  DashboardPage,
  InboxPage: MyInboxPage,
  TasksPage: CoreTaskListPage,
  TeamsPage: TeamManagementPage,
  QueuesPage: CoreQueueManager as React.ComponentType<unknown>,
  PoliciesPage: CorePolicyListPage,
  MetricsPage: CoreMetricListPage,
  MasterWorkflowPage,
  WorkflowStudioPage,
  PrimitivesPage: UnifiedPrimitivePage,
  TemplatesPage: WorkflowListPage,
  FederationPage: SubgraphConstellation,
  ComponentExplorerPage,
  ErrorMonitorPage,
  DocumentStudioPage,  // backward compat — old nav items still resolve
  IntakeStudioPage,    // DRR-137: canonical name for new seeds
  PdfStudioPage,
  WorkflowDetailPage,
  NotificationListPage,
  NotificationsPage: NotificationListPage,
  CoreReportListPage,
  AlertListPage,

  // Subgraph pages — all route through generic data-driven components (DRR-055)
  PayrollPage,
  PatientSafetyPage: PatientSafetyDashboard,  // cross-subgraph dashboard (pages/admin/)

  // Accounting subgraph (DRR-045) — SubgraphBrowserPage reads subgraph from URL
  AccountingPage: SubgraphBrowserPage,
  JournalEntryCreatePage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  JournalEntryEditPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  JournalEntryViewPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  JournalEntryApprovePage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  JournalEntriesPage: SubgraphBrowserPage,

  // Healthcare / FHIR subgraph (DRR-055) — entity type comes from URL params
  HealthcarePage: SubgraphBrowserPage,
  PatientCreatePage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PatientEditPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PatientViewPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PatientApprovePage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PractitionerCreatePage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PractitionerEditPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PractitionerViewPage: SubgraphEntityFormPage as React.ComponentType<unknown>,

  // Payroll entity pages — null placeholders (MetadataRoutes handles payroll routing)
  TimeEntryCreatePage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  TimeEntryEditPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  TimeEntryViewPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  TimeEntryApprovePage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PayrollRunCreatePage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PayrollRunEditPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PayrollRunViewPage: SubgraphEntityFormPage as React.ComponentType<unknown>,
  PayrollRunApprovePage: SubgraphEntityFormPage as React.ComponentType<unknown>,

  // Core pages referenced by backend seed data (ferp-core config + seed_subgraph_data.rs)
  WorkflowListPage,
  MyDocumentsPage,
  DocumentsPage: MyDocumentsPage,
  RoleAccessComparisonPage,
  ApprovalsPage: ApprovalListPage,

  // Primitive list pages (backend references by "{Primitive}sPage" or "{Primitive}ListPage")
  StatesPage: StateListPage,
  StateListPage,
  EventsPage: EventListPage,
  EventListPage,
  SagasPage: SagaListPage,
  SagaListPage,
  DAGNodeListPage,
  AlertsPage: AlertListPage,
  ActorsPage: ActorListPage,
  RolesPage: RoleListPage,
  CompliancePage: ComplianceListPage,
  RisksPage: RiskListPage,
  EvidencePage: EvidenceListPage,

  // Admin/architect pages (backend references by component name)
  FormSchemaAdminPage,
  SubgraphEntityManagerPage,
  DataSourceBrowserPage,
  ConstellationPage,
  JourneyPage,
  GanttPage,
  CalendarPage,
  WorkflowOrchestrationPage,
  WorkflowStatePage: WorkflowStateView,
  SourceMapListPage,
  StaffManagementPage: ActorManagementPage as React.ComponentType<unknown>,

  // Architect layer pages
  PrimitiveConstellationPage: CorePrimitiveConstellationPage as React.ComponentType<unknown>,
  LlmFormSchemaEditorPage,
  FormGalleryPage,
  WorkflowRegistryPage,
  LlmWorkflowEditorPage,
  TenantOnboardingPage: TenantOnboardingPageComponent,
  ProductCatalogPage,
  ScopeHierarchyPage,
  MyAccessPage,
  WorkflowObserverPage,
  DevDashboardPage,
  RegistryExplorerPage,
  ReferenceLibraryPage,  // DRR-245: Staff-facing policy lookup (read-only)
  ReferencePage: ReferenceLibraryPage,  // alias for nav pattern
  ExchangePage,
  LedgerDashboardPage,
  LedgerFamilyPage,
  LedgerAuditPage,

  // Universal Subgraph Browser — data-driven entity browsing with Tabulator
  // All subgraph-specific pages route through this generic browser (DRY).
  // SubgraphMetadata provides entity types, fields, and permissions;
  // SubgraphBrowserPage renders them via Tabulator + FormSchema pipeline.
  SubgraphBrowserPage,

  // ── EDI Subgraph (all route through generic SubgraphBrowserPage) ────
  EdiClaimsPage: SubgraphBrowserPage,
  EdiRemittancePage: SubgraphBrowserPage,
  EdiEligibilityPage: SubgraphBrowserPage,
  EdiAcknowledgmentsPage: SubgraphBrowserPage,
  EdiPartnersPage: SubgraphBrowserPage,

  // ── FHIR Subgraph (all route through generic SubgraphBrowserPage) ───
  PatientsPage: SubgraphBrowserPage,
  EncountersPage: SubgraphBrowserPage,
  ObservationsPage: SubgraphBrowserPage,
  MedicationsPage: SubgraphBrowserPage,
  ClaimsPage: SubgraphBrowserPage,
  // FHIR User layer care management (ALF terminology: residents, care plans)
  ResidentsPage: SubgraphBrowserPage,
  CarePlansPage: SubgraphBrowserPage,
  AssessmentsPage: SubgraphBrowserPage,
  IncidentsPage: SubgraphBrowserPage,
  ConditionsPage: SubgraphBrowserPage,
  AllergyIntolerancesPage: SubgraphBrowserPage,

  // ── Accounting Subgraph (additional pages) ──────────────────────────
  GlAccountsPage: SubgraphBrowserPage,
  VendorsPage: SubgraphBrowserPage,
  CustomersPage: SubgraphBrowserPage,
  FinancialReportsPage: SubgraphBrowserPage,

  // ── Scheduling Subgraph ─────────────────────────────────────────────
  MySchedulePage: SubgraphBrowserPage,
  AvailabilityPage: SubgraphBrowserPage,
  SwapRequestsPage: SubgraphBrowserPage,
  SchedulesPage: SubgraphBrowserPage,
  CoveragePage: SubgraphBrowserPage,
  SchedulingRulesPage: SubgraphBrowserPage,

  // ── Benefits Subgraph ───────────────────────────────────────────────
  BenefitsDashboardPage: SubgraphBrowserPage,
  EnrollmentPage: SubgraphBrowserPage,
  EligibilityPage: SubgraphBrowserPage,
  LifeEventsPage: SubgraphBrowserPage,
  PlansPage: SubgraphBrowserPage,
  EnrollmentsPage: SubgraphBrowserPage,
  COBRAPage: SubgraphBrowserPage,
  ComplianceConfigPage: SubgraphBrowserPage,

  // ── Time-Tracking Subgraph ──────────────────────────────────────────
  PunchClockPage: SubgraphBrowserPage,
  TimesheetPage: SubgraphBrowserPage,
  PTORequestsPage: SubgraphBrowserPage,
  AdminTimesheetsPage: SubgraphBrowserPage,
  TimePoliciesPage: SubgraphBrowserPage,

  // ── Website Administration (DRR-107: admin settings only, no CMS) ──
  // Relocated to pages/admin/ — not a federation subgraph page
  WebsiteSettingsPage,

  // ── Wearables Subgraph (Vuzix smart glasses) ──────────────────────
  WearableMyDevicePage: SubgraphBrowserPage,
  WearableDevicesPage: SubgraphBrowserPage,
  WearableSessionsPage: SubgraphBrowserPage,
  WearableCapturesPage: SubgraphBrowserPage,
  WearableCommandsPage: SubgraphBrowserPage,
  WearableBeaconsPage: SubgraphBrowserPage,
  WearableProximityPage: SubgraphBrowserPage,

  // ── FHIR User Layer (eMAR + Documents) ────────────────────────
  MedicationAdministrationPage: SubgraphBrowserPage,
  FhirDocumentsPage: SubgraphBrowserPage,

  // Regulatory Intelligence Pipeline (DRR-058)
  RegulatoryRulesPage,

  // RuleSet Legislation Viewer (DRR-090: accessible to all roles)
  RuleSetViewerPage,

  // RuleSet Admin — Import / Supersede / Sunset (DRR-096/112, regulatory:import scope)
  RuleSetAdminPage,

  // Standards Pack curator (DRR-112 compliance packs)
  StandardsPackPage,

  // DRR-207: CONFIGURE section pages (Data vs Configuration separation)
  // Form Overrides — jurisdiction-specific field additions/validations
  FormSchemaOverrideListPage,
  FormOverridesPage: FormSchemaOverrideListPage,  // alias for nav pattern matching
  FormSchemaOverrideDetailPage,  // /configure/form-overrides/:subgraph/:entityType
  // Regulatory — governance dashboard, coverage matrix
  RegulatoryGovernancePage,
  RegulatoryPage: RegulatoryGovernancePage,  // alias for nav pattern matching
  // Regulatory Overview — jurisdiction coverage dashboard (DRR-207 main page)
  RegulatoryOverviewPage,
  ConfigureRegulatoryPage: RegulatoryOverviewPage,  // /configure/regulatory route

  // Witness queue — Tier 4 dual verification (DRR-063)
  WitnessQueuePage,

  // Offline chain sync + conflict resolution (DRR-113)
  SyncDashboardPage,

  // BLE beacon + elopement safety monitoring (DRR-100/108)
  BeaconDashboardPage,

  // Config edge care graph browser (DRR-110, architect tool)
  ConfigEdgeBrowserPage,

  // Config type registry explorer (DRR-111, replaces hardcoded constants)
  // NOTE: RegistryExplorerPage already registered above from '@/pages/architect/RegistryExplorerPage'
  // DevRegistryExplorerPage aliases it from '@/pages/dev' (same component)
  DevRegistryExplorerPage,

  // Template version history (DRR-089)
  TemplateVersionPage,

  // EXECUTE Phase Primitives
  TaskListPage,
  TaskTemplatesPage,

  // Training pages (wf-0-0-006: Staff Training Lifecycle)
  MyTrainingPage,             // Self-service training dashboard
  StaffTrainingPage,          // Supervisor training management
  TrainingComplianceDashboard, // DRR-221 CompletionManager progress viz
  TrainingPage: StaffTrainingPage,  // Alias for nav pattern matching

  // ── Workforce Subgraph (@ferp/workforce) ──────────────────────
  WorkforceDashboardPage,
  // Employees
  EmployeeListPage: WfEmployeeListPage,
  EmployeeCreatePage: WfEmployeeCreatePage,
  EmployeeDetailPage: WfEmployeeDetailPage,
  EmployeeEditPage: WfEmployeeEditPage,
  // Payroll
  PayrollListPage: WfPayrollListPage,
  PayrollRunPage: WfPayrollRunPage,
  PayrollRunDetailPage: WfPayrollRunDetailPage,
  PayStubListPage: WfPayStubListPage,
  PayStubDetailPage: WfPayStubDetailPage,
  AmendmentListPage: WfAmendmentListPage,
  PayPeriodListPage: WfPayPeriodListPage,
  // Time & Attendance
  TimeAttendancePage: WfTimeAttendancePage,
  GpsPunchPage: WfGpsPunchPage,
  WhosInPage: WfWhosInPage,
  ExceptionsPage: WfExceptionsPage,
  TimesheetVerificationPage: WfTimesheetVerificationPage,
  // Schedules
  SchedulePage: WfSchedulePage,
  ScheduleCalendarPage: WfScheduleCalendarPage,
  ScheduleTemplatesPage: WfScheduleTemplatesPage,
  ScheduleTemplatePage: WfScheduleTemplatePage,
  // Leave & Requests
  LeaveBalancePage: WfLeaveBalancePage,
  RequestListPage: WfRequestListPage,
  RequestCreatePage: WfRequestCreatePage,
  // Policies
  PolicyListPage: WfPolicyListPage,
  PolicyDetailPage: WfPolicyDetailPage,
  AccrualPolicyPage: WfAccrualPolicyPage,
  // Company
  CompanyPage: WfCompanyPage,
  CompanySettingsPage: WfCompanySettingsPage,
  BranchListPage: WfBranchListPage,
  DepartmentListPage: WfDepartmentListPage,
  // HR
  QualificationListPage: WfQualificationListPage,
  KpiListPage: WfKpiListPage,
  ReviewListPage: WfReviewListPage,
  ReviewDetailPage: WfReviewDetailPage,
  // Reports
  ReportsPage: WfReportsPage,
  ReportListPage: WfReportListPage,
  ReportResultPage: WfReportResultPage,
  // Documents
  DocumentListPage: WfDocumentListPage,
  DocumentDetailPage: WfDocumentDetailPage,
  EmployeeDocumentsPage: WfEmployeeDocumentsPage,
  // GEO Fencing
  GeoFenceListPage: WfGeoFenceListPage,
  GeoFenceDetailPage: WfGeoFenceDetailPage,
  GeoFenceViolationsPage: WfGeoFenceViolationsPage,
  // Job Costing
  JobListPage: WfJobListPage,
  JobDetailPage: WfJobDetailPage,
  JobCostReportPage: WfJobCostReportPage,
  // Invoicing
  InvoiceListPage: WfInvoiceListPage,
  InvoiceDetailPage: WfInvoiceDetailPage,
  // Expenses
  ExpenseListPage: WfExpenseListPage,
  ExpenseReportPage: WfExpenseReportPage,
  // Recruitment
  JobPostingListPage: WfJobPostingListPage,
  ApplicantDetailPage: WfApplicantDetailPage,
  InterviewSchedulePage: WfInterviewSchedulePage,
  // Onboarding
  OnboardingListPage: WfOnboardingListPage,
  OnboardingDetailPage: WfOnboardingDetailPage,
  SmartOnboardingPage: WfSmartOnboardingPage,
  // Tax Filing
  TaxFilingCenterPage: WfTaxFilingCenterPage,
  // Settings
  AuditLogPage: WfAuditLogPage,
  MyAccountPage: WfMyAccountPage,
  ImportExportPage: WfImportExportPage,

  // ── Food Service Subgraph (DRR-280: Ghost Kitchen Operations) ─────
  StationView,
  ExpoQueueView,
  KitchenManagerDashboard,
  DriverDeliveryView,
  // Aliases for nav patterns
  KitchenStationPage: StationView,
  ExpoPage: ExpoQueueView,
  KitchenManagerPage: KitchenManagerDashboard,
  DeliveryPage: DriverDeliveryView,

  // ── CFSS Subgraph (DRR-279: Community First Services & Supports) ──
  CfssDashboardPage,
  CfssTimesheetApprovalPage,
  CfssSupervisionPage,
  CfssPayrollPage,
  CfssWorkerManagementPage,
  CfssServiceAuthPage,
  CfssTrainingPage,
  CfssDocumentsPage,
  CfssIncidentPage,
  CfssCommunicationLogPage,
  // Aliases for nav patterns
  CfssHomePage: CfssDashboardPage,
  CfssTimesheetsPage: CfssTimesheetApprovalPage,
  CfssSupervisionVisitsPage: CfssSupervisionPage,
  CfssPayrollRunPage: CfssPayrollPage,
  CfssWorkersPage: CfssWorkerManagementPage,
  CfssAuthorizationsPage: CfssServiceAuthPage,
  CfssTrainingCompliancePage: CfssTrainingPage,
  CfssDocsPage: CfssDocumentsPage,
  CfssIncidentReportPage: CfssIncidentPage,
  CfssContactLogPage: CfssCommunicationLogPage,
};

/**
 * Get a component by name, with fallback to placeholder.
 *
 * @param componentName - The component name from SubgraphMetadata
 * @returns The corresponding React component or a placeholder
 */
export function getComponent(componentName: string): React.ComponentType<unknown> {
  const component = COMPONENT_MAP[componentName];

  if (!component) {
    // Return a placeholder that shows which component is missing
    const MissingComponent = () => React.createElement(PlaceholderPage, { componentName });
    MissingComponent.displayName = `MissingComponent(${componentName})`;
    return MissingComponent;
  }

  return component;
}

export default COMPONENT_MAP;
